import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Element } from 'react-bulma-components';
import AnimatedChevronIcon from '../../ui/animated-chevron-icon/animated-chevron-icon';
import Button from '../../ui/button/button';
import styles from './filter-bar-item.module.scss';

export type FilterBarItemProps = {
  title: string;
  appliedCount: number;
  onClear: () => void;
  onSubmit: () => void;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
};

export function FilterBarItem({
  title,
  appliedCount,
  onClear,
  onSubmit,
  children,
  icon,
  className = '',
}: FilterBarItemProps) {
  const [active, setActive] = useState<boolean>(false);
  const [filterButtonPressed, setFilterButtonPressed] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number }>({ top: 0 });

  const modalClassName = useMemo(() => {
    const defaultClassName = `${styles.floatingDiv}`;
    if (filterButtonPressed) {
      return `${defaultClassName} ${
        active ? `${styles.active}` : `${styles.hidden}`
      }`;
    } else {
      return `${defaultClassName} is-hidden`;
    }
  }, [active, filterButtonPressed]);

  const handleFilterOnClick: React.MouseEventHandler<HTMLDivElement> =
    useCallback(
      (e) => {
        e.stopPropagation();
        if (!filterButtonPressed) {
          setFilterButtonPressed(true);
        }
        setActive((prevActive) => {
          return !prevActive;
        });
      },
      [filterButtonPressed]
    );

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' && !active) || (e.key === 'Escape' && active)) {
        handleFilterOnClick(e as unknown as React.MouseEvent<HTMLDivElement>);
      }
    },
    [active, handleFilterOnClick]
  );

  const handleSubmit = useCallback(() => {
    onSubmit();
    setActive(false);
  }, [onSubmit]);

  const handleClear = useCallback(() => {
    onClear();
    setActive(false);
  }, [onClear]);

  useEffect(() => {
    if (active && itemRef.current) {
      setPosition({
        top: itemRef.current.offsetHeight + 3,
      });
    }
  }, [active]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        active &&
        floatingRef.current &&
        !floatingRef.current.contains(target) &&
        itemRef.current &&
        !itemRef.current.contains(target)
      ) {
        setActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [active]);

  return (
    <div
      className={`${styles.filterContainer} ${className}`}
      onClick={handleFilterOnClick}
      onKeyDown={handleOnKeyDown}
      tabIndex={0}
      ref={itemRef}
    >
      <div className={`${styles.item} ${active ? styles.activeItem : ''}`}>
        {icon && <div className={styles.iconContainer}>{icon}</div>}
        <span
          className={`has-text-primary-dark is-underlined ${
            appliedCount > 0 ? 'has-text-weight-bold' : ''
          }`}
        >
          {title}
          {appliedCount > 0 && ` (${appliedCount})`}
        </span>
        <AnimatedChevronIcon
          active={active}
          className="ml-2 has-text-primary-dark"
        />
      </div>

      <div
        ref={floatingRef}
        className={modalClassName}
        style={{
          top: position.top,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalContent}>
          <Element display="flex" flexDirection="column" className="gap-4">
            {children}
          </Element>

          <Element display="flex" className="gap-6" justifyContent="flex-end">
            <Button
              color="primary-dark"
              outlined
              className="is-borderless"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button color="primary-dark" onClick={handleSubmit}>
              Apply
            </Button>
          </Element>
        </div>
      </div>
    </div>
  );
}

export default FilterBarItem;
